import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { ChevronDown } from 'react-feather'
import useSWR, { mutate } from 'swr'
import { EntityName } from 'src/lib/hooks/useActivityLogs'
import Loader from 'src/components/common/loader-components/Loader'
import { apiURI } from 'src/lib/utils/constants'
import { postRequest, staticOptions } from 'src/lib/core/api-client'
import { createDeepCopy } from 'src/lib/utils/helpers'
import {
  formatTimestamp,
  groupByDate,
  roundToMidnight,
  roundToEndOfDay,
  toPascalCase
} from 'src/lib/utils/activityLogsHelpers'

interface ActivityLogType {
  merchant_id: string
  action_name: string
  entity_name: string
  role: string
  store_user_id: string
  timestamp: string
  user_email: string
  user_name: string
}

const ActivityLogs = () => {
  const [weeksLoaded, setWeeksLoaded] = useState(1)
  const [hasMoreData, setHasMoreData] = useState(true)
  const [groupedData, setGroupedData] = useState<Record<string, ActivityLogType[]>>({})

  const payload = useMemo(() => {
    const currentDate = new Date()
    const startDate = new Date(currentDate.getTime() - weeksLoaded * 7 * 24 * 60 * 60 * 1000)
    const endDate = new Date(currentDate.getTime() - (weeksLoaded - 1) * 7 * 24 * 60 * 60 * 1000)

    return {
      startDate: roundToMidnight(startDate),
      endDate: roundToEndOfDay(endDate),
      filters: {
        entity_name: Object.values(EntityName)
      }
    }
  }, [weeksLoaded])

  const { data: fetchedData, isValidating: isLoading } = useSWR(
    ['/dashboard/activity-logs', apiURI.ACTIVITY_LOG_GET],
    ([url, baseUrl]) => postRequest(url, baseUrl, payload),
    staticOptions
  )

  useEffect(() => {
    if (isLoading) return
    if (Boolean((fetchedData as any)?.data?.params?.data?.length)) {
      const tempData: Record<string, ActivityLogType[]> =
        groupedData && Object.keys(groupedData).length ? createDeepCopy(groupedData) : {}
      setGroupedData(() => {
        return { ...tempData, ...groupByDate((fetchedData as any)?.data?.params?.data) }
      })
      setHasMoreData(true)
      return
    }
    setHasMoreData(false)
  }, [fetchedData, isLoading])

  const handleLoadMore = useCallback(() => {
    if (hasMoreData) {
      setWeeksLoaded(prev => prev + 1)
    }
  }, [hasMoreData])

  useEffect(() => {
    if (weeksLoaded > 1) {
      mutate(['/dashboard/activity-logs', apiURI.ACTIVITY_LOG_GET])
    }
  }, [weeksLoaded])

  return (
    <div className='w-full shadow-md rounded-sm flex flex-col gap-4 p-4'>
      {Object.values(groupedData).map((logs, groupIdx) => (
        <div key={groupIdx} className='flex flex-col gap-3'>
          {logs.map((log, idx) => (
            <div key={idx} className='flex flex-col gap-1 leading-4'>
              <p className='text-sm font-normal text-[#6E6E6E]'>{formatTimestamp(log.timestamp)}</p>
              <p className='text-sm font-medium'>
                {toPascalCase(log.user_name)} {log.action_name} in {log.entity_name.toLocaleLowerCase()}
              </p>
            </div>
          ))}
          {groupIdx < Object.values(groupedData).length - 1 && (
            <hr className='border-t-1 border-gray-200 max-w-[90%]' />
          )}
        </div>
      ))}
      {hasMoreData && !isLoading && (
        <div
          className='flex justify-between text-base text-start font-semibold text-primary border-1 p-3 rounded-t-none border-gray-200 rounded-sm cursor-pointer'
          onClick={handleLoadMore}
        >
          <p>Load more</p>
          <ChevronDown />
        </div>
      )}
      {isLoading && <Loader />}
    </div>
  )
}

export default ActivityLogs
